import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import './media/developer.css.js';
import { VSBuffer } from 'vscode/vscode/vs/base/common/buffer';
import { Disposable, MutableDisposable, combinedDisposable, dispose } from 'vscode/vscode/vs/base/common/lifecycle';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { Categories } from 'vscode/vscode/vs/platform/action/common/actionCommonCategories';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { IOpenerService } from 'vscode/vscode/vs/platform/opener/common/opener.service';
import { IQuickInputService } from 'vscode/vscode/vs/platform/quickinput/common/quickInput.service';
import { ITerminalLogService } from 'vscode/vscode/vs/platform/terminal/common/terminal.service';
import { IWorkspaceContextService } from 'vscode/vscode/vs/platform/workspace/common/workspace.service';
import { registerTerminalAction } from '../../../terminal/browser/terminalActions.js';
import { registerTerminalContribution } from '../../../terminal/browser/terminalExtensions.js';
import { TerminalContextKeys } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminalContextKey';
import { IStatusbarService } from 'vscode/vscode/vs/workbench/services/statusbar/browser/statusbar.service';

var DevModeContribution_1;
const _moduleId = "vs/workbench/contrib/terminalContrib/developer/browser/terminal.developer.contribution";
registerTerminalAction({
    id: "workbench.action.terminal.showTextureAtlas" ,
    title: ( localize2WithPath(_moduleId, 0, 'Show Terminal Texture Atlas')),
    category: Categories.Developer,
    precondition: ( (ContextKeyExpr.or(TerminalContextKeys.isOpen))),
    run: async (c, accessor) => {
        const fileService = accessor.get(IFileService);
        const openerService = accessor.get(IOpenerService);
        const workspaceContextService = accessor.get(IWorkspaceContextService);
        const bitmap = await c.service.activeInstance?.xterm?.textureAtlas;
        if (!bitmap) {
            return;
        }
        const cwdUri = workspaceContextService.getWorkspace().folders[0].uri;
        const fileUri = URI.joinPath(cwdUri, 'textureAtlas.png');
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext('bitmaprenderer');
        if (!ctx) {
            return;
        }
        ctx.transferFromImageBitmap(bitmap);
        const blob = await ( (new Promise((res) => canvas.toBlob(res))));
        if (!blob) {
            return;
        }
        await fileService.writeFile(fileUri, VSBuffer.wrap(( (new Uint8Array(await blob.arrayBuffer())))));
        openerService.open(fileUri);
    }
});
registerTerminalAction({
    id: "workbench.action.terminal.writeDataToTerminal" ,
    title: ( localize2WithPath(_moduleId, 1, 'Write Data to Terminal')),
    category: Categories.Developer,
    run: async (c, accessor) => {
        const quickInputService = accessor.get(IQuickInputService);
        const instance = await c.service.getActiveOrCreateInstance();
        await c.service.revealActiveTerminal();
        await instance.processReady;
        if (!instance.xterm) {
            throw ( (new Error('Cannot write data to terminal if xterm isn\'t initialized')));
        }
        const data = await quickInputService.input({
            value: '',
            placeHolder: 'Enter data, use \\x to escape',
            prompt: ( localizeWithPath(
                _moduleId,
                2,
                "Enter data to write directly to the terminal, bypassing the pty"
            )),
        });
        if (!data) {
            return;
        }
        let escapedData = data
            .replace(/\\n/g, '\n')
            .replace(/\\r/g, '\r');
        while (true) {
            const match = escapedData.match(/\\x([0-9a-fA-F]{2})/);
            if (match === null || match.index === undefined || match.length < 2) {
                break;
            }
            escapedData = escapedData.slice(0, match.index) + String.fromCharCode(parseInt(match[1], 16)) + escapedData.slice(match.index + 4);
        }
        const xterm = instance.xterm;
        xterm._writeText(escapedData);
    }
});
registerTerminalAction({
    id: "workbench.action.terminal.restartPtyHost" ,
    title: ( localize2WithPath(_moduleId, 3, 'Restart Pty Host')),
    category: Categories.Developer,
    run: async (c, accessor) => {
        const logService = accessor.get(ITerminalLogService);
        const backends = Array.from(c.instanceService.getRegisteredBackends());
        const unresponsiveBackends = backends.filter(e => !e.isResponsive);
        const restartCandidates = unresponsiveBackends.length > 0 ? unresponsiveBackends : backends;
        for (const backend of restartCandidates) {
            logService.warn(`Restarting pty host for authority "${backend.remoteAuthority}"`);
            backend.restartPtyHost();
        }
    }
});
let DevModeContribution = class DevModeContribution extends Disposable {
    static { DevModeContribution_1 = this; }
    static { this.ID = 'terminal.devMode'; }
    static get(instance) {
        return instance.getContribution(DevModeContribution_1.ID);
    }
    constructor(_instance, processManager, widgetManager, _configurationService, _statusbarService) {
        super();
        this._instance = _instance;
        this._configurationService = _configurationService;
        this._statusbarService = _statusbarService;
        this._activeDevModeDisposables = ( (new MutableDisposable()));
        this._currentColor = 0;
        this._statusbarEntryAccessor = this._register(( (new MutableDisposable())));
        this._register(this._configurationService.onDidChangeConfiguration(e => {
            if (e.affectsConfiguration("terminal.integrated.developer.devMode" )) {
                this._updateDevMode();
            }
        }));
    }
    xtermReady(xterm) {
        this._xterm = xterm;
        this._updateDevMode();
    }
    _updateDevMode() {
        const devMode = this._isEnabled();
        this._xterm?.raw.element?.classList.toggle('dev-mode', devMode);
        const commandDetection = this._instance.capabilities.get(2 );
        if (devMode) {
            if (commandDetection) {
                const commandDecorations = ( (new Map()));
                this._activeDevModeDisposables.value = combinedDisposable(
                this._instance.onDidBlur(() => this._updateDevMode()), this._instance.onDidFocus(() => this._updateDevMode()), commandDetection.promptInputModel.onDidChangeInput(() => this._updateDevMode()),
                commandDetection.onCommandFinished(command => {
                    const colorClass = `color-${this._currentColor}`;
                    const decorations = [];
                    commandDecorations.set(command, decorations);
                    if (command.promptStartMarker) {
                        const d = this._instance.xterm.raw?.registerDecoration({
                            marker: command.promptStartMarker
                        });
                        if (d) {
                            decorations.push(d);
                            d.onRender(e => {
                                e.textContent = 'A';
                                e.classList.add('xterm-sequence-decoration', 'top', 'left', colorClass);
                            });
                        }
                    }
                    if (command.marker) {
                        const d = this._instance.xterm.raw?.registerDecoration({
                            marker: command.marker,
                            x: command.startX
                        });
                        if (d) {
                            decorations.push(d);
                            d.onRender(e => {
                                e.textContent = 'B';
                                e.classList.add('xterm-sequence-decoration', 'top', 'right', colorClass);
                            });
                        }
                    }
                    if (command.executedMarker) {
                        const d = this._instance.xterm.raw?.registerDecoration({
                            marker: command.executedMarker,
                            x: command.executedX
                        });
                        if (d) {
                            decorations.push(d);
                            d.onRender(e => {
                                e.textContent = 'C';
                                e.classList.add('xterm-sequence-decoration', 'bottom', 'left', colorClass);
                            });
                        }
                    }
                    if (command.endMarker) {
                        const d = this._instance.xterm.raw?.registerDecoration({
                            marker: command.endMarker
                        });
                        if (d) {
                            decorations.push(d);
                            d.onRender(e => {
                                e.textContent = 'D';
                                e.classList.add('xterm-sequence-decoration', 'bottom', 'right', colorClass);
                            });
                        }
                    }
                    this._currentColor = (this._currentColor + 1) % 2;
                }), commandDetection.onCommandInvalidated(commands => {
                    for (const c of commands) {
                        const decorations = commandDecorations.get(c);
                        if (decorations) {
                            dispose(decorations);
                        }
                        commandDecorations.delete(c);
                    }
                }));
                this._updatePromptInputStatusBar(commandDetection);
            }
            else {
                this._activeDevModeDisposables.value = this._instance.capabilities.onDidAddCapabilityType(e => {
                    if (e === 2 ) {
                        this._updateDevMode();
                    }
                });
            }
        }
        else {
            this._activeDevModeDisposables.clear();
        }
    }
    _isEnabled() {
        return this._configurationService.getValue("terminal.integrated.developer.devMode" ) || false;
    }
    _updatePromptInputStatusBar(commandDetection) {
        const promptInputModel = commandDetection.promptInputModel;
        if (promptInputModel) {
            const name = ( localizeWithPath(_moduleId, 4, 'Terminal Dev Mode'));
            const isExecuting = promptInputModel.cursorIndex === -1;
            this._statusbarEntry = {
                name,
                text: `$(${isExecuting ? 'loading~spin' : 'terminal'}) ${promptInputModel.getCombinedString()}`,
                ariaLabel: name,
                tooltip: 'The detected terminal prompt input',
                kind: 'prominent'
            };
            if (!this._statusbarEntryAccessor.value) {
                this._statusbarEntryAccessor.value = this._statusbarService.addEntry(this._statusbarEntry, `terminal.promptInput.${this._instance.instanceId}`, 0 );
            }
            else {
                this._statusbarEntryAccessor.value.update(this._statusbarEntry);
            }
            this._statusbarService.updateEntryVisibility(`terminal.promptInput.${this._instance.instanceId}`, this._instance.hasFocus);
        }
    }
};
DevModeContribution = DevModeContribution_1 = ( (__decorate([
    ( (__param(3, IConfigurationService))),
    ( (__param(4, IStatusbarService)))
], DevModeContribution)));
registerTerminalContribution(DevModeContribution.ID, DevModeContribution);
